import { CaseMilestone, isOptionalMilestone } from '@/hooks/useCaseMilestones';
import { AlertTriangle, Clock, ArrowRight, Calendar } from 'lucide-react';
import { MilestoneIcon } from '@/utils/milestoneIcons';

interface CaseStageSidebarProps {
  milestones: CaseMilestone[];
}

export default function CaseStageSidebar({ milestones }: CaseStageSidebarProps) {
  const actionRequired = milestones.filter(m => m.status_category === 'action_required');
  // Etapa opcional ainda não usada não é "etapa atual" nem "próxima".
  const isIdleOptional = (m: CaseMilestone) =>
    m.status_category === 'not_started' && isOptionalMilestone(m);
  const currentStage = milestones.find(m =>
    m.status_category !== 'completed' && m.status_category !== 'failed' && !isIdleOptional(m)
  );
  const currentIdx = currentStage ? milestones.indexOf(currentStage) : -1;
  const nextStage = currentIdx >= 0
    ? milestones.slice(currentIdx + 1).find(m => m.status_category === 'not_started' && !isIdleOptional(m))
    : null;
  const externalWait = milestones.filter(m => m.status_category === 'external_wait');

  if (!currentStage && actionRequired.length === 0 && externalWait.length === 0) return null;

  return (
    <div className="space-y-3">
      <h3 className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-2">
        Próximas Ações
      </h3>

      {/* Action required items - red urgency */}
      {actionRequired.map(m => (
        <div
          key={m.id}
          className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30"
        >
          <MilestoneIcon icon={m.template?.icon} size={16} />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-rose-700 dark:text-rose-300">{m.name}</div>
            {m.current_status && (
              <div className="text-[9px] font-mono text-muted-foreground">
                {m.current_status.replace(/_/g, ' ')}
              </div>
            )}
          </div>
        </div>
      ))}

      {/* External waits - blue */}
      {externalWait.map(m => (
        <div
          key={m.id}
          className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/30"
        >
          <MilestoneIcon icon={m.template?.icon} size={16} />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-blue-700 dark:text-blue-300">{m.name}</div>
            {m.current_status && (
              <div className="text-[9px] font-mono text-muted-foreground">
                {m.current_status.replace(/_/g, ' ')}
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Current stage - amber (internal action) */}
      {currentStage && currentStage.status_category !== 'action_required' && currentStage.status_category !== 'external_wait' && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30">
          <MilestoneIcon icon={currentStage.template?.icon} size={16} />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-amber-700 dark:text-amber-300">{currentStage.name}</div>
            {currentStage.current_status && (
              <div className="text-[9px] font-mono text-muted-foreground">
                {currentStage.current_status.replace(/_/g, ' ')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Next stage - gray/muted */}
      {nextStage && (
        <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-border bg-muted/30">
          <MilestoneIcon icon={nextStage.template?.icon} size={16} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold text-muted-foreground uppercase">Próxima Etapa</div>
            <div className="text-[11px] font-medium text-foreground">{nextStage.name}</div>
            {nextStage.template?.estimated_duration_days && (
              <div className="text-[9px] font-mono text-muted-foreground">
                ~{nextStage.template.estimated_duration_days} dias
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
